import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

/**
 * Capture les erreurs de rendu React pour éviter l'écran blanc total.
 * Affiche le message d'erreur (utile en phase de test/dev pour diagnostiquer)
 * et propose de recharger l'application.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    // eslint-disable-next-line no-console
    console.error('Erreur de rendu capturée par ErrorBoundary :', error, info);
  }

  private readonly handleReload = (): void => {
    window.location.assign('/');
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      const { error, componentStack } = this.state;
      return (
        <main className="flex min-h-screen items-center justify-center bg-cream px-4 py-8">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-8 shadow-lg ring-1 ring-slate-200">
            <h1 className="text-lg font-semibold text-slate-900">Une erreur est survenue</h1>
            <p className="mt-2 text-sm text-slate-500">
              L'affichage de cette page a échoué. Vous pouvez recharger l'application sans perdre
              votre session.
            </p>

            {error && import.meta.env.DEV && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-left">
                <div className="text-xs font-semibold text-red-800">{error.name}</div>
                <div className="mt-1 break-words font-mono text-xs text-red-700">
                  {error.message}
                </div>
                {(error.stack || componentStack) && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-[11px] text-red-600">
                      Détails techniques
                    </summary>
                    <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap text-[10px] leading-relaxed text-red-700">
                      {componentStack ?? error.stack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={this.handleReload}
              className="mt-6 rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand/50"
            >
              Recharger
            </button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}
